import React, { useState, useCallback } from 'react';
import Header from './components/Header';
import DraftArea from './components/DraftArea';
import BookPreview from './components/BookPreview';
import TableOfContents from './components/TableOfContents';
import { extractHeadings } from './utils/bookUtils';

function App() {
  const [parts, setParts] = useState([]);
  const [bookTitle, setBookTitle] = useState('');
  const [bookAuthor, setBookAuthor] = useState('');
  const [showToc, setShowToc] = useState(false);

  const handleAddPart = useCallback((content) => {
    const newPart = {
      id: Date.now().toString() + Math.random().toString(36).substring(7),
      content,
      timestamp: new Date().toISOString()
    };
    setParts((prev) => [...prev, newPart]);
  }, []);

  const handleRemovePart = useCallback((id) => {
    setParts((prev) => prev.filter(part => part.id !== id));
  }, []);

  const handleReorderParts = useCallback((newParts) => {
    setParts(newParts);
  }, []);

  const handleClearAll = useCallback(() => {
    if (window.confirm('Are you sure you want to clear your entire book draft?')) {
      setParts([]);
      setBookTitle('');
      setBookAuthor('');
    }
  }, []);

  const buildFinalMarkdown = useCallback(() => {
    const lines = [];
    if (bookTitle) {
      lines.push(`# ${bookTitle}`);
      if (bookAuthor) lines.push(`\n*by ${bookAuthor}*`);
      lines.push('\n---\n');
    }
    const headings = extractHeadings(parts);
    if (headings.length > 0) {
      lines.push('## Table of Contents\n');
      headings.forEach((h, i) => { lines.push(`${i + 1}. ${h.text}`); });
      lines.push('\n---\n');
    }
    parts.forEach(p => lines.push(p.content));
    return lines.join('\n');
  }, [parts, bookTitle, bookAuthor]);

  const handleDownload = useCallback(() => {
    if (parts.length === 0) {
      alert('Please add some content to your book before downloading.');
      return;
    }
    try {
      const finalMarkdown = buildFinalMarkdown();
      const blob = new Blob(['\ufeff', finalMarkdown], { type: 'text/markdown;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStr = new Date().toISOString().split('T')[0];
      const safeName = bookTitle
        ? bookTitle.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        : 'ebook-draft';
      link.href = url;
      link.download = `${safeName}-${dateStr}.md`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to generate download. Please try again.');
    }
  }, [parts, buildFinalMarkdown, bookTitle]);

  const headings = extractHeadings(parts);

  return (
    <div className="h-screen bg-slate-100 flex flex-col font-sans overflow-hidden">
      <Header
        onDownload={handleDownload}
        onClear={handleClearAll}
        partCount={parts.length}
        bookTitle={bookTitle}
        bookAuthor={bookAuthor}
        onTitleChange={setBookTitle}
        onAuthorChange={setBookAuthor}
        onToggleToc={() => setShowToc(v => !v)}
        showToc={showToc}
        hasHeadings={headings.length > 0}
      />

      <main className="flex-1 min-h-0 max-w-[1700px] w-full mx-auto p-4 md:p-6 grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Drafting Pad — sticky, never scrolls */}
        <section className="lg:col-span-4 min-h-0 flex flex-col">
          <DraftArea onAddPart={handleAddPart} />
        </section>

        {/* TOC panel */}
        {showToc && headings.length > 0 && (
          <section className="lg:col-span-2 min-h-0 flex flex-col">
            <TableOfContents headings={headings} />
          </section>
        )}

        {/* Book Preview — only this scrolls */}
        <section className={`${showToc && headings.length > 0 ? 'lg:col-span-6' : 'lg:col-span-8'} min-h-0 flex flex-col`}>
          <BookPreview
            parts={parts}
            onRemovePart={handleRemovePart}
            onReorderParts={handleReorderParts}
            bookTitle={bookTitle}
            bookAuthor={bookAuthor}
          />
        </section>
      </main>
    </div>
  );
}

export default App;